import { ToastAction, type ToastActionElement } from '@/components/ui/toast';
import { GUEST_TRIAL_ENDED_TITLE } from '@/credits/guest-trial-cta';

/**
 * Shape passed to `toast()` — mirrors the fields the builder uses. Kept local
 * so this helper has no dependency on the toast hook's internals.
 */
export interface CreditAwareToastOptions {
  title: string;
  description: string;
  variant: 'destructive';
  duration: number;
  action?: ToastActionElement;
}

export interface CreditAwareErrorToastInput {
  /** Human-readable error message from the failed request. */
  message: string;
  /** Error code from the API (e.g. `'INSUFFICIENT_CREDITS'`), when present. */
  code?: string;
  /** Toast title for non-credit errors (e.g. `'Error'`, `'Publish Failed'`). */
  fallbackTitle: string;
  /** Invoked by the "Buy more credits" CTA — typically `navigate('/credits')`. */
  onBuyCredits: () => void;
  /**
   * Guest-trial account: a credit shortfall means the free trial is spent, and
   * a guest cannot buy credits — the CTA becomes "Create free account"
   * (typically `navigate('/auth/signup?reason=trial-ended')`). Their app and
   * session stay theirs when they sign up (same account, upgraded in place).
   */
  guest?: { onCreateAccount: () => void };
}

// One home for the guest sign-up targets (CreditExhaustionListener and the
// inline credit messages use the same constants).
export {
  GUEST_CREATE_ACCOUNT_SIGNUP_PATH,
  GUEST_TRIAL_ENDED_SIGNUP_PATH,
} from '@/credits/guest-trial-cta';

/**
 * Build the toast options for a failed builder request, branching on whether
 * the failure is a credit shortfall.
 *
 * A credit shortfall is user-actionable, not an internal fault: instead of a
 * dead-end error that drops the user out of whatever they were doing (building
 * OR publishing), surface a friendly prompt with a one-click path to top up.
 * Shared by the streaming `onError` handler and the publish handler so both
 * flows behave identically.
 */
export function creditAwareErrorToast(input: CreditAwareErrorToastInput): CreditAwareToastOptions {
  if (input.code === 'INSUFFICIENT_CREDITS' && input.guest) {
    const { onCreateAccount } = input.guest;
    return {
      title: GUEST_TRIAL_ENDED_TITLE,
      description:
        'Create your free account to keep building. This app and everything in it stay yours.',
      variant: 'destructive',
      duration: 15000,
      action: (
        <ToastAction altText="Create free account" onClick={onCreateAccount}>
          Create free account
        </ToastAction>
      ),
    };
  }
  if (input.code === 'INSUFFICIENT_CREDITS') {
    return {
      title: 'Out of credits',
      description: input.message,
      variant: 'destructive',
      duration: 15000,
      action: (
        <ToastAction altText="Buy more credits" onClick={input.onBuyCredits}>
          Buy more credits
        </ToastAction>
      ),
    };
  }
  // Typed transient-provider failures (stability overhaul 1a): retryable and
  // NOT the user's fault — say so plainly instead of "internal error". The
  // inline Retry affordance lives in the conversation pane; the toast is the
  // immediate acknowledgement.
  if (input.code === 'PROVIDER_TIMEOUT') {
    return {
      title: 'The AI timed out',
      description: input.message,
      variant: 'destructive',
      duration: 10000,
    };
  }
  if (input.code === 'PROVIDER_OVERLOADED') {
    return {
      title: 'AI provider is busy',
      description: input.message,
      variant: 'destructive',
      duration: 10000,
    };
  }
  // The gateway's per-user 24h spend ceiling: not retryable, not the user's
  // code, and not an "internal error" (which is how it read for a full day).
  if (input.code === 'DAILY_BURN_CAP_EXCEEDED') {
    return {
      title: 'Daily spending limit reached',
      description: input.message,
      variant: 'destructive',
      duration: 12000,
    };
  }
  return {
    title: input.fallbackTitle,
    description: input.message,
    variant: 'destructive',
    duration: 8000,
  };
}
